
import { defineComponent } from "vue";
import Alerter from "@/components/Alerter.vue";
import { Alert } from "@/commons/types";

import {
  DeviceHistorySchema,
  DeviceHistoryCreateSchema,
  DeviceHistoryUpdateSchema,
  DeviceHistoriesService,
  VehiclesService,
  VehicleSchema,
  OrganizationSchema,
  OrganizationsService,
} from "@/commons/client";

export default defineComponent({
  name: "DeviceHistoryForm",
  components: { Alerter },

  props: {
    serialNumber: { type: String, required: true },
    moboSerialNumber: { type: String, required: true },
  },

  data: () => ({
    alerts: [] as Alert[],

    // Organizations
    organizations: [] as OrganizationSchema[],
    orgSearchKey: "",
    loadingOrganizations: false,
    organizationSelectedId: null as number | null,

    // Vehicles
    vehicles: [] as VehicleSchema[],
    vehicleSearchKey: "",
    loadingVehicles: false,
    vehicleSelectedId: null as number | null,
    noVehicle: false,

    // Device History
    deviceHistory: {} as DeviceHistorySchema,
    saving: false,
  }),

  computed: {
    rules(): any {
      return this.$store.state.genericRules;
    },
    deviceHistoryLoaded(): boolean {
      return !!this.deviceHistory?.uid;
    },
    deviceHistoryCreateData(): DeviceHistoryCreateSchema {
      return {
        current_device_id: this.serialNumber,
        organization_id: this.organizationSelectedId as number,
        // se 'noVehicle' è attivo => undefined
        current_vehicle_id: this.noVehicle
          ? undefined
          : this.vehicleSelectedId != null
          ? String(this.vehicleSelectedId)
          : undefined,
      };
    },
    deviceHistoryUpdateData(): DeviceHistoryUpdateSchema {
      // parto dall’esistente per non perdere altri campi
      const data: any = { ...this.deviceHistory };
      data.organization_id = this.organizationSelectedId as number;
      data.current_device_id = this.serialNumber;
      data.current_vehicle_id = this.noVehicle
        ? null
        : this.vehicleSelectedId ?? null;
      return data as DeviceHistoryUpdateSchema;
    },
  },

  methods: {
    formatDate(dateStr: string): string {
      try {
        return new Date(dateStr).toLocaleDateString("it-IT");
      } catch {
        return dateStr;
      }
    },

    // --- ORGANIZATIONS ---
    async fetchOrganizations(): Promise<void> {
      if (this.organizations.length > 0) return;
      this.loadingOrganizations = true;
      try {
        // opzionale: passare pagination/filters nel tuo backend
        this.organizations =
          await OrganizationsService.organizationListV1OrganizationsGet(
            undefined,
            undefined,
            this.orgSearchKey || undefined
          );
      } catch {
        this.alerts.push({
          type: "error",
          text: "Errore durante il recupero delle aziende",
        });
      } finally {
        this.loadingOrganizations = false;
      }
    },

    onOrganizationChange(): void {
      // cambio azienda -> reset veicoli & selection
      this.vehicles = [];
      this.vehicleSelectedId = null;
      this.noVehicle = false;
      if (this.organizationSelectedId) {
        this.fetchVehiclesForOrganization(this.organizationSelectedId);
      }
    },

    // --- VEHICLES ---
    async fetchVehiclesForOrganization(orgId: number): Promise<void> {
      this.loadingVehicles = true;
      try {
        this.vehicles =
          await VehiclesService.organizationVehiclesListV1OrganizationsPkVehiclesGet(
            orgId,
            undefined, // page
            100, // page_size
            this.vehicleSearchKey || undefined
          );
      } catch {
        this.alerts.push({
          type: "error",
          text: "Errore durante il recupero dei veicoli",
        });
      } finally {
        this.loadingVehicles = false;
      }
    },

    onNoVehicleToggle(): void {
      if (this.noVehicle) {
        this.vehicleSelectedId = null;
      }
    },

    // --- DEVICE HISTORY ---
    async loadExistingDeviceHistory(): Promise<void> {
      try {
        const dh =
          await DeviceHistoriesService.deviceHistoryRetrieveByMoboSerialV1DeviceHistoriesByMoboSerialMoboSerialGet(
            this.moboSerialNumber
          );
        if (dh && dh.uid) {
          this.deviceHistory = dh;
          // Prefill organization + vehicle correnti
          this.organizationSelectedId = dh.organization_id as number;
          this.noVehicle = dh.current_vehicle_id == null;
          this.vehicleSelectedId = this.noVehicle
            ? null
            : (dh.current_vehicle_id as any);

          // Carica veicoli per l’azienda pre-selezionata
          if (this.organizationSelectedId) {
            await this.fetchVehiclesForOrganization(
              this.organizationSelectedId
            );
          }
        }
      } catch {
        // Nessuna history: lascio il form in stato "create"
        this.deviceHistory = {} as DeviceHistorySchema;
      }
    },

    async createOrUpdateDeviceHistory(): Promise<void> {
      if (!this.organizationSelectedId) return;

      this.saving = true;
      try {
        if (this.deviceHistoryLoaded) {
          // UPDATE: richiede (pk, organizationId, body)
          const res =
            await DeviceHistoriesService.deviceHistoryUpdateV1DeviceHistoriesPkPut(
              this.deviceHistory.uid,
              this.organizationSelectedId,
              this.deviceHistoryUpdateData
            );
          this.deviceHistory = res;
          this.alerts.push({
            type: "success",
            text: "Associazione aggiornata con successo",
          });
        } else {
          // CREATE
          await DeviceHistoriesService.deviceHistoryCreateV1DeviceHistoriesPost(
            this.deviceHistoryCreateData
          );
          this.alerts.push({
            type: "success",
            text: "Associazione creata con successo",
          });
          // Ricarico lo stato attuale (utile per vedere UID e valori aggiornati)
          await this.loadExistingDeviceHistory();
        }
      } catch (err) {
        this.alerts.push({
          type: "error",
          text: "Errore nel salvataggio dell’associazione",
        });
      } finally {
        this.saving = false;
      }
    },
  },

  watch: {
    // watcher corretto: prima era 'search'
    orgSearchKey() {
      // se vuoi abilitare ricerca live delle aziende:
      // debounce consigliato, qui richiamiamo on-demand con il bottone "Cerca" se lo aggiungi
    },
    vehicleSearchKey() {
      // idem per veicoli; se vuoi, richiama fetchVehiclesForOrganization con debounce
    },
  },

  async mounted() {
    await this.fetchOrganizations();
    await this.loadExistingDeviceHistory();
    // Se sto creando e non esiste history, puoi preforzare l’azienda in base al contesto (opzionale)
  },
});
